import { useCallback } from 'react';

import { useAppSelector, useAppDispatch } from 'store/hooks/useStore';
import { useToast } from 'hooks/useToast';
import * as customerApi from 'api/customerApi';
import {
  editActions,
  editSelectors,
} from 'store/reducers/customerReducer/editSlice';
import { contractListActions } from 'store/reducers/customerReducer/contractListSlice';
import {
  listActions,
  listSelectors,
} from 'store/reducers/customerReducer/listSlice';
import { UpdateCustomer } from 'types/customer';
import { loaderActions } from 'store/reducers/loaderReducer/loaderSlice';
import { CustomerFormFields } from '../types/customerFormField';

/**
 * 顧客編集 store の実装をコンポーネントから隠すための中間層
 * @returns selector と action のラッパー
 */
export const useEditStore = () => {
  const dispatch = useAppDispatch();
  const toast = useToast();

  const loading = useAppSelector(editSelectors.loading);
  const isOpenInputModal = useAppSelector(editSelectors.isOpenInputModal);
  const isOpenConfirmationModal = useAppSelector(
    editSelectors.isOpenConfirmationModal,
  );
  const isOpenDeleteModal = useAppSelector(editSelectors.isOpenDeleteModal);
  const id = useAppSelector(editSelectors.id);
  const name = useAppSelector(editSelectors.name);
  const detail = useAppSelector(editSelectors.detail);
  const environment = useAppSelector(editSelectors.environment);
  const environmentType = useAppSelector(editSelectors.environmentType);
  const corporateNumber = useAppSelector(editSelectors.corporateNumber);
  const channel = useAppSelector(editSelectors.channel);
  const poseidonUrl = useAppSelector(editSelectors.poseidonUrl);
  const status = useAppSelector(editSelectors.status);
  const systemUpdateGroup = useAppSelector(editSelectors.systemUpdateGroup);
  const iapClientId = useAppSelector(editSelectors.iapClientId);
  const iapClientSecret = useAppSelector(editSelectors.iapClientSecret);
  const currentSystemVersion = useAppSelector(
    editSelectors.currentSystemVersion,
  );
  const targetSystemVersion = useAppSelector(editSelectors.targetSystemVersion);
  const poseidonAgentCpu = useAppSelector(editSelectors.poseidonAgentCpu);
  const poseidonAgentMemory = useAppSelector(editSelectors.poseidonAgentMemory);
  const enableVerticalPodAutoScaler = useAppSelector(
    editSelectors.enableVerticalPodAutoScaler,
  );
  const zeusToken = useAppSelector(editSelectors.zeusToken);
  const zeusTokenCreatedAt = useAppSelector(editSelectors.zeusTokenCreatedAt);
  const zeusTokenExpired = useAppSelector(editSelectors.zeusTokenExpired);
  const customerDetailFetchedTime = useAppSelector(
    editSelectors.customerDetailFetchedTime,
  );

  // deleteNotification 用
  const selectedCustomer = useAppSelector(listSelectors.selectedCustomer);

  const init = useCallback(() => {
    dispatch(editActions.init());
  }, [dispatch]);

  const openModal = useCallback(() => {
    dispatch(editActions.modalOpened());
  }, [dispatch]);

  const goInputModal = useCallback(() => {
    dispatch(editActions.stepMovedToInput());
  }, [dispatch]);

  const goConfirmationModal = useCallback(() => {
    dispatch(editActions.stepMovedToConfirmation());
  }, [dispatch]);

  const goDeleteModal = useCallback(() => {
    dispatch(editActions.stepMovedToDelete());
  }, [dispatch]);

  const setForm = useCallback(
    (form: CustomerFormFields) => {
      dispatch(
        editActions.formGroupUpdated({
          form,
        }),
      );
    },
    [dispatch],
  );

  const putCustomerAndReload = useCallback(async () => {
    if (!id) {
      return;
    }
    dispatch(loaderActions.setLoading(true));
    dispatch(editActions.loadStarted());
    const params: UpdateCustomer = {
      name,
      detail,
      environment,
      environmentType,
      corporateNumber,
      channel,
      poseidonUrl,
      systemUpdateGroup,
      iapClientId,
      iapClientSecret,
      status,
      poseidonAgentCpu: poseidonAgentCpu || undefined,
      poseidonAgentMemory: poseidonAgentMemory || undefined,
      enableVerticalPodAutoScaler,
    };

    const result = await customerApi.putCustomer(id, params);
    dispatch(editActions.loadEnded());
    if (result.ok) {
      // 顧客一覧をリロードするために最終更新日を更新する
      dispatch(
        listActions.finalUpdateTimeUpdated({
          finalUpdateTime: new Date().toISOString(),
        }),
      );
      toast({
        status: 'success',
        description: '顧客の更新に成功しました。',
      });
      // 初期化
      dispatch(editActions.init());
      dispatch(contractListActions.init());
      dispatch(listActions.selectedCustomerCleared());
    } else {
      toast({
        status: 'error',
        description: `顧客の更新に失敗しました。: ${result.err.message}`,
      });
    }
    dispatch(loaderActions.setLoading(false));
  }, [
    id,
    dispatch,
    name,
    detail,
    environment,
    environmentType,
    corporateNumber,
    channel,
    poseidonUrl,
    systemUpdateGroup,
    iapClientId,
    iapClientSecret,
    status,
    poseidonAgentCpu,
    poseidonAgentMemory,
    enableVerticalPodAutoScaler,
    toast,
  ]);

  const deleteCustomerAndReload = useCallback(async () => {
    if (!selectedCustomer) {
      return;
    }

    dispatch(loaderActions.setLoading(true));
    const result = await customerApi.deleteCustomer(selectedCustomer.id);

    if (result.ok) {
      // 顧客一覧をリロードするために最終更新日を更新する
      dispatch(
        listActions.finalUpdateTimeUpdated({
          finalUpdateTime: new Date().toISOString(),
        }),
      );
      toast({
        status: 'success',
        description: '顧客の削除に成功しました。',
      });
      // 初期化
      dispatch(editActions.init());
      dispatch(contractListActions.init());
      dispatch(listActions.selectedCustomerCleared());
    } else {
      toast({
        status: 'error',
        description: `顧客の削除に失敗しました。: ${result.err.message}`,
      });
    }
    dispatch(loaderActions.setLoading(false));
  }, [selectedCustomer, dispatch, toast]);

  const fetchCustomer = useCallback(async () => {
    if (!selectedCustomer) {
      return;
    }

    dispatch(editActions.loadStarted());
    const resultCustomer = await customerApi.fetchCustomer(selectedCustomer.id);
    const resultZeusToken = await customerApi.fetchZeusToken(
      selectedCustomer.id,
    );

    if (!resultCustomer.ok) {
      toast({
        status: 'error',
        description: `顧客の取得に失敗しました: ${resultCustomer.err.message}`,
      });
      dispatch(editActions.loadEnded());

      return;
    }

    if (!resultZeusToken.ok) {
      toast({
        status: 'error',
        description: `Zeus Token の取得に失敗しました: ${resultZeusToken.err.message}`,
      });
      dispatch(editActions.loadEnded());

      return;
    }

    dispatch(
      editActions.fetchCustomerSuccess({
        id: selectedCustomer.id,
        status: selectedCustomer.status,
        originalCustomerDetail: resultCustomer.value,
        originalZeusToken: resultZeusToken.value,
        customerDetailFetchedTime: new Date().toISOString(),
      }),
    );

    dispatch(editActions.loadEnded());
  }, [dispatch, selectedCustomer, toast]);

  return {
    loading,
    isOpenInputModal,
    isOpenConfirmationModal,
    isOpenDeleteModal,
    id,
    name,
    detail,
    environment,
    environmentType,
    corporateNumber,
    channel,
    poseidonUrl,
    status,
    systemUpdateGroup,
    iapClientId,
    iapClientSecret,
    currentSystemVersion,
    targetSystemVersion,
    poseidonAgentCpu,
    poseidonAgentMemory,
    enableVerticalPodAutoScaler,
    zeusToken,
    zeusTokenCreatedAt,
    zeusTokenExpired,
    customerDetailFetchedTime,
    init,
    openModal,
    goInputModal,
    goConfirmationModal,
    goDeleteModal,
    setForm,
    fetchCustomer,
    putCustomerAndReload,
    deleteCustomerAndReload,
  };
};
